import { useSiteSettings } from '@/contexts/SiteSettingsContext';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

export function PremiumReportPage() {
  const { links } = useSiteSettings();
  const [activeTab, setActiveTab] = useState(0);

  const handleTabKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      setActiveTab((index + 1) % 2);
      e.preventDefault();
    }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      setActiveTab((index + 1) % 2); // since there are only 2 tabs, index+1 % 2 works both ways
      e.preventDefault();
    }
  };

  return (
    <>
      <Helmet>
        <title>Premium Personal Feedback Report | FIVE LENS Enneagram</title>
        <meta name="description" content="Comprehensive personal development report mapping Enneagram, vertical development, resilience, social drives, and decision-making styles." />
        <meta property="og:title" content="Premium Personal Feedback Report | FIVE LENS Enneagram" />
        <meta property="og:description" content="Comprehensive personal development report mapping Enneagram, vertical development, resilience, social drives, and decision-making styles." />
        <meta name="twitter:title" content="Premium Personal Feedback Report | FIVE LENS Enneagram" />
        <meta name="twitter:description" content="Comprehensive personal development report mapping Enneagram, vertical development, resilience, social drives, and decision-making styles." />
      </Helmet>

      <div className="pfrp pt-20">
        <header className="pfrp__hero">
          <div className="pfrp__wrap">
            <nav className="pfrp-tier" aria-label="Report versions">
              <span className="pfrp-tier__label">Report version</span>
              <Link className="pfrp-tier__item" to="/reports/standard">&larr; Standard</Link>
              <span className="pfrp-tier__item pfrp-tier__item--on" aria-current="page">Premium</span>
            </nav>
            <p className="pfrp__eyebrow">FIVE LENS &middot; Personal Feedback Report Premium &middot; Preview</p>
            <h1>Every reading comes with a page of questions.</h1>
            <p className="pfrp__lede">The Premium report reads you across the same five lenses as the Standard report. What it adds is the work: a facing page of self-coaching questions for each factor, plus four extra chapters on how you show up in a team, how you lead, and what you do when you don't get what you want.</p>

            <div className="pfrp-pair">
              <div className="pfrp-pair__side">
                <span className="pfrp-pair__tag">In both reports</span>
                <h3>The reading</h3>
                <p>Where you sit on the scale, what that suggests, and what it tends to look like in practice.</p>
                <div style={{ marginTop: '18px' }}>
                  <div className="pfrp-scale__anchors"><span>Low</span><span>High</span></div>
                  <div className="pfrp-scale__band" style={{ '--hue': 'var(--rule-5)' } as React.CSSProperties}></div>
                  <div className="pfrp-scale__rule"><div className="pfrp-scale__marker" style={{ '--pos': '72%' } as React.CSSProperties}><span>M</span></div></div>
                </div>
              </div>
              <div className="pfrp-pair__link" aria-hidden="true"><span>then &mdash;&mdash;&mdash;&gt;</span></div>
              <div className="pfrp-pair__side pfrp-pair__side--new">
                <span className="pfrp-pair__tag">Premium only</span>
                <h3>The questions</h3>
                <p>A full page of self-coaching questions on that same factor, each with a short note on why it's being asked and what to look for in your answer.</p>
                <div style={{ marginTop: '18px', display: 'grid', gap: '8px' }}>
                  <div style={{ height: '9px', borderRadius: '2px', background: 'repeating-linear-gradient(115deg,var(--ground-deep) 0 6px,var(--ground) 6px 12px)' }}></div>
                  <div style={{ height: '9px', width: '78%', borderRadius: '2px', background: 'repeating-linear-gradient(115deg,var(--ground-deep) 0 6px,var(--ground) 6px 12px)' }}></div>
                  <div style={{ height: '9px', width: '88%', borderRadius: '2px', background: 'repeating-linear-gradient(115deg,var(--ground-deep) 0 6px,var(--ground) 6px 12px)' }}></div>
                </div>
              </div>
            </div>

            <ul className="pfrp__hero-meta">
              <li>Premium report</li>
              <li>5 lenses</li>
              <li>43 pages</li>
              <li>9 chapters</li>
              <li>Questionnaire: about 20 minutes</li>
              <li>Sample profile: Rachel Seymour</li>
            </ul>
          </div>
        </header>

        <section className="pfrp__section">
          <div className="pfrp__wrap">
            <div className="pfrp__head">
              <div className="pfrp__head-row">
                <p className="pfrp__eyebrow">01 &mdash; The difference</p>
                <div className="pfrp__ticks"></div>
              </div>
              <h2>What Premium adds to the Standard report</h2>
              <p className="pfrp__lede">Both reports measure the same things. Premium doesn't give you more scales &mdash; it gives you more to do with them.</p>
            </div>

            <div className="overflow-x-auto w-full">
              <table className="pfrp-table min-w-[700px]">
                <thead>
                  <tr>
                    <th scope="col">What's in the report</th>
                    <th scope="col">Standard</th>
                    <th scope="col">Premium</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">All five lenses, read in full</th>
                    <td><span className="pfrp-yes">Included</span></td>
                    <td><span className="pfrp-yes">Included</span></td>
                  </tr>
                  <tr>
                    <th scope="row">Your primary, secondary and least-used Enneagram pattern</th>
                    <td><span className="pfrp-yes">Included</span></td>
                    <td><span className="pfrp-yes">Included</span></td>
                  </tr>
                  <tr>
                    <th scope="row">Self-coaching questions on every mastery factor and on resilience</th>
                    <td><span className="pfrp-no">&mdash;</span></td>
                    <td><span className="pfrp-yes">Added</span></td>
                  </tr>
                  <tr>
                    <th scope="row">Your two mastery dimensions and an overall mastery reading</th>
                    <td><span className="pfrp-no">&mdash;</span></td>
                    <td><span className="pfrp-yes">Added</span></td>
                  </tr>
                  <tr>
                    <th scope="row">Me as a team member &mdash; two roles, and how each behaves as a team forms and settles</th>
                    <td><span className="pfrp-no">&mdash;</span></td>
                    <td><span className="pfrp-yes">Added</span></td>
                  </tr>
                  <tr>
                    <th scope="row">Me as a leader &mdash; your style at its best and at its worst</th>
                    <td><span className="pfrp-no">&mdash;</span></td>
                    <td><span className="pfrp-yes">Added</span></td>
                  </tr>
                  <tr>
                    <th scope="row">How you cope with disappointment, and how you move around other people</th>
                    <td><span className="pfrp-no">&mdash;</span></td>
                    <td><span className="pfrp-yes">Added</span></td>
                  </tr>
                  <tr>
                    <th scope="row">Length</th>
                    <td>24 pages</td>
                    <td>43 pages</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="pfrp-table__note">Same questionnaire, same twenty minutes. The choice is about how much you plan to do with the result.</p>
          </div>
        </section>

        <section className="pfrp__section">
          <div className="pfrp__wrap">
            <div className="pfrp__head">
              <div className="pfrp__head-row">
                <p className="pfrp__eyebrow">02 &mdash; How a factor is worked</p>
                <div className="pfrp__ticks"></div>
              </div>
              <h2>One factor, two pages</h2>
              <p className="pfrp__lede">Switch between them. The first page tells you where you sit. The second stops describing and starts asking. The written feedback is held back here &mdash; that part belongs in your own report.</p>
            </div>

            <div className="pfrp-tabs" role="tablist" aria-label="Two pages for one factor">
              <button 
                className="pfrp-tab" 
                role="tab" 
                aria-selected={activeTab === 0} 
                onClick={() => setActiveTab(0)}
                onKeyDown={(e) => handleTabKeyDown(e, 0)}
                type="button"
              >
                Page one &mdash; your reading
              </button>
              <button 
                className="pfrp-tab" 
                role="tab" 
                aria-selected={activeTab === 1} 
                onClick={() => setActiveTab(1)}
                onKeyDown={(e) => handleTabKeyDown(e, 1)}
                type="button"
              >
                Page two &mdash; your questions
              </button>
            </div>

            <article className="pfrp-page" role="tabpanel" hidden={activeTab !== 0}>
              <div className="pfrp-page__bar">
                <div className="pfrp-page__brand">FIVE LENS<span className="pfrp-page__tier">Premium</span></div>
                <div className="pfrp-page__title">
                  <strong>Factor D: Curiosity</strong>
                  <span>Are you willing to learn new things?</span>
                </div>
              </div>
              <div className="pfrp-page__cols">
                <div className="pfrp-page__col">
                  <p className="pfrp-page__label">Your reading</p>
                  <div className="pfrp-scale__band" style={{ '--hue': 'var(--rule-5)' } as React.CSSProperties}></div>
                  <div className="pfrp-scale__rule"><div className="pfrp-scale__marker" style={{ '--pos': '74%' } as React.CSSProperties}><span>M</span></div></div>
                  <div className="pfrp-page__anchors">
                    <div><b>Closed</b><ul><li>Rigid attitude</li><li>Avoidant</li><li>Unquestioning</li></ul></div>
                    <div><b>Open</b><ul><li>Flexible attitude</li><li>Engaging</li><li>Questioning</li></ul></div>
                  </div>
                  <ul className="pfrp-redact">
                    <li><span></span><span style={{ width: '86%' }}></span></li>
                    <li><span></span><span style={{ width: '62%' }}></span></li>
                    <li><span></span><span style={{ width: '77%' }}></span></li>
                  </ul>
                  <p className="pfrp-page__stamp">Four or five statements about what this reading tends to look like day to day.</p>
                </div>
                <div className="pfrp-page__col">
                  <p className="pfrp-page__label">What this could mean to you</p>
                  <ul className="pfrp-redact">
                    <li><span></span><span></span><span style={{ width: '54%' }}></span></li>
                    <li><span></span><span style={{ width: '70%' }}></span></li>
                    <li><span></span><span></span><span style={{ width: '41%' }}></span></li>
                  </ul>
                  <p className="pfrp-page__stamp">The consequences &mdash; where this shows up at work, in decisions, and in how others experience you.</p>
                </div>
                <div className="pfrp-page__col pfrp-page__col--aside">
                  <p className="pfrp-page__label">Insights</p>
                  <ul className="pfrp-redact"><li><span></span><span></span><span style={{ width: '58%' }}></span></li></ul>
                  <p className="pfrp-page__stamp" style={{ marginBottom: '4px' }}>Context from the research behind this factor.</p>
                  <p className="pfrp-page__label" style={{ marginTop: '24px' }}>Reflections</p>
                  <ul className="pfrp-redact"><li><span style={{ width: '92%' }}></span></li><li><span style={{ width: '78%' }}></span></li></ul>
                  <p className="pfrp-page__stamp">A few opening questions to sit with.</p>
                </div>
              </div>
            </article>

            <article className="pfrp-page" role="tabpanel" hidden={activeTab !== 1}>
              <div className="pfrp-page__bar">
                <div className="pfrp-page__brand">FIVE LENS<span className="pfrp-page__tier">Premium</span></div>
                <div className="pfrp-page__title">
                  <strong>Factor D: Curiosity</strong>
                  <span>Developmental questions</span>
                </div>
              </div>
              <div className="pfrp-page__cols pfrp-page__cols--wide">
                <div className="pfrp-page__col">
                  <p className="pfrp-page__label">Self coaching</p>
                  <ol className="pfrp-q">
                    <li>
                      <b>A question about where this habit came from.</b>
                      <div className="pfrp-lines"><span></span><span style={{ width: '64%' }}></span></div>
                    </li>
                    <li>
                      <b>A question about how it shows up at work.</b>
                      <div className="pfrp-lines"><span></span><span style={{ width: '81%' }}></span></div>
                    </li>
                    <li>
                      <b>A question about what it costs you.</b>
                      <div className="pfrp-lines"><span></span><span style={{ width: '55%' }}></span></div>
                    </li>
                    <li>
                      <b>A question about what you'd do differently.</b>
                      <div className="pfrp-lines"><span></span><span style={{ width: '73%' }}></span></div>
                    </li>
                  </ol>
                  <p className="pfrp-page__stamp">Six to eight questions per factor. Under each one, a short note explaining why it's being asked and what an honest answer usually reveals.</p>
                </div>
                <div className="pfrp-page__col pfrp-page__col--aside">
                  <p className="pfrp-page__label">On this page</p>
                  <p style={{ fontSize: '15px', color: 'var(--ink-2)' }}>The factor being worked is highlighted on the mastery wheel, so you can see which part of the picture you're in. The questions are yours to answer &mdash; in writing, with a coach, or over several sittings.</p>
                  <p className="pfrp-page__stamp">One of these pages follows each of the six mastery factors, and one follows the resilience chapter.</p>
                </div>
              </div>
            </article>
          </div>
        </section>

        <section className="pfrp__section">
          <div className="pfrp__wrap">
            <div className="pfrp__head">
              <div className="pfrp__head-row">
                <p className="pfrp__eyebrow">03 &mdash; The extra chapters</p>
                <div className="pfrp__ticks"></div>
              </div>
              <h2>Six things only the Premium report tells you</h2>
            </div>

            <ol className="pfrp-chapters">
              <li className="pfrp-chapter pfrp-chapter--1">
                <div className="pfrp-chapter__num">Ch. 01</div>
                <div>
                  <h3>Your two dimensions</h3>
                  <p className="pfrp-chapter__q"><em>Are you pushing, or accepting?</em></p>
                </div>
                <div className="pfrp-chapter__what">
                  <p>The six mastery factors split into two groups: the ones about going out and changing things, and the ones about living with what you can't change. Premium reads each group separately, then gives an overall figure. Most people find one side well ahead of the other, and that gap is usually the most practical thing in the report.</p>
                  <ul className="pfrp-chapter__chips"><li>Active</li><li>Passive</li><li>Overall reading</li></ul>
                </div>
              </li>
              <li className="pfrp-chapter pfrp-chapter--2">
                <div className="pfrp-chapter__num">Ch. 02</div>
                <div>
                  <h3>Self-coaching pages</h3>
                  <p className="pfrp-chapter__q"><em>What are you going to do about it?</em></p>
                </div>
                <div className="pfrp-chapter__what">
                  <p>Seven pages of questions, each tied to a factor you've just read about. They're deliberately uncomfortable in places. Answered properly, over weeks, they're the part of the report that actually changes something &mdash; and they give a coach a ready-made agenda.</p>
                  <ul className="pfrp-chapter__chips"><li>Six mastery factors</li><li>+ resilience</li></ul>
                </div>
              </li>
              <li className="pfrp-chapter pfrp-chapter--3">
                <div className="pfrp-chapter__num">Ch. 03</div>
                <div>
                  <h3>Me as a team member</h3>
                  <p className="pfrp-chapter__q"><em>What role do you fall into?</em></p>
                </div>
                <div className="pfrp-chapter__what">
                  <p>The role you take in a group, described for both your primary and your secondary pattern &mdash; and how that role behaves as a team forms, argues, settles into rules, and finally gets on with the work. Useful reading before a team programme, and blunt about where you're likely to frustrate people.</p>
                  <ul className="pfrp-chapter__chips"><li>Primary role</li><li>Secondary role</li><li>Four team stages</li></ul>
                </div>
              </li>
              <li className="pfrp-chapter pfrp-chapter--4">
                <div className="pfrp-chapter__num">Ch. 04</div>
                <div>
                  <h3>Me as a leader</h3>
                  <p className="pfrp-chapter__q"><em>What happens when you're in charge?</em></p>
                </div>
                <div className="pfrp-chapter__what">
                  <p>Your leadership style described twice: at your best, and at your less-than-best. Then a set of paired opposites &mdash; the tensions your particular style lives between &mdash; with questions to place yourself honestly between them. It applies whether or not you currently manage anyone.</p>
                  <ul className="pfrp-chapter__chips"><li>At your best</li><li>At your worst</li><li>Leadership questions</li></ul>
                </div>
              </li>
              <li className="pfrp-chapter pfrp-chapter--5">
                <div className="pfrp-chapter__num">Ch. 05</div>
                <div>
                  <h3>How you handle disappointment</h3>
                  <p className="pfrp-chapter__q"><em>What do you do when you don't get what you want?</em></p>
                </div>
                <div className="pfrp-chapter__what">
                  <p>The nine patterns fall into three coping groups: some people look for the bright side, some go quiet and get competent, and some need the feeling expressed. Premium tells you which group you're in and how strongly &mdash; which explains a great deal about how you behave in a setback.</p>
                  <ul className="pfrp-chapter__chips"><li>Positive outlook</li><li>Competency</li><li>Emotional realness</li></ul>
                </div>
              </li>
              <li className="pfrp-chapter pfrp-chapter--6">
                <div className="pfrp-chapter__num">Ch. 06</div>
                <div>
                  <h3>How you move around people</h3>
                  <p className="pfrp-chapter__q"><em>Towards, against, or away?</em></p>
                </div>
                <div className="pfrp-chapter__what">
                  <p>Under pressure, people move in one of three directions: they push, they comply, or they withdraw. Premium reads where you sit and how far. It's a short chapter and often the one people remember, because it names something they've done their whole lives without noticing.</p>
                  <ul className="pfrp-chapter__chips"><li>Assertive</li><li>Compliant</li><li>Withdrawn</li></ul>
                </div>
              </li>
            </ol>
          </div>
        </section>

        <section className="pfrp__section">
          <div className="pfrp__wrap">
            <div className="pfrp__head">
              <div className="pfrp__head-row">
                <p className="pfrp__eyebrow">04 &mdash; Choosing</p>
                <div className="pfrp__ticks"></div>
              </div>
              <h2>Which report do you actually need?</h2>
            </div>

            <div className="pfrp-choose">
              <div>
                <h3>Standard is enough when&hellip;</h3>
                <ul>
                  <li>You want the readings and a single debrief conversation.</li>
                  <li>It's a first exposure to the Enneagram, for a whole group at once.</li>
                  <li>The report is one input into something else &mdash; a workshop, a selection conversation, a team session.</li>
                  <li>You'd rather start light and go deeper later.</li>
                </ul>
              </div>
              <div>
                <h3>Premium earns its place when&hellip;</h3>
                <ul>
                  <li>The report feeds a coaching programme running over months.</li>
                  <li>The person leads a team, or is being prepared to.</li>
                  <li>You want something to work through on your own between sessions, not just read once.</li>
                  <li>The question isn't "what am I like" but "what do I change first".</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="pfrp__section">
          <div className="pfrp__wrap">
            <div className="pfrp__head">
              <div className="pfrp__head-row">
                <p className="pfrp__eyebrow">05 &mdash; How it runs</p>
                <div className="pfrp__ticks"></div>
              </div>
              <h2>From questionnaire to something you actually use</h2>
            </div>

            <ol className="pfrp-steps">
              <li><div>
                <b>Answer the questionnaire</b>
                <p>Online, roughly twenty minutes, the same for both reports. Answer as you actually are rather than as you'd like to be &mdash; the report is only as useful as the honesty you put into it.</p>
              </div></li>
              <li><div>
                <b>Receive your report</b>
                <p>A private PDF, addressed to you. It isn't sent to your manager or your HR team unless you've agreed to that upfront.</p>
              </div></li>
              <li><div>
                <b>Read it once, straight through</b>
                <p>Skip the question pages on this pass. Note what stings &mdash; the lines that irritate you are usually the ones worth returning to.</p>
              </div></li>
              <li><div>
                <b>Debrief with a practitioner</b>
                <p>Usually 60 to 90 minutes. A certified practitioner takes you through the readings, checks them against your own experience, and helps you pick the one or two worth acting on. <Link to="/for-individuals#request-intro" className="text-rule-3 underline font-medium hover:text-ink transition-colors">Request a debrief</Link> or <Link to="/find-a-practitioner" className="text-rule-3 underline font-medium hover:text-ink transition-colors">find an accredited coach</Link>.</p>
              </div></li>
              <li><div>
                <b>Work the question pages</b>
                <p>This is the part Premium exists for. Take one factor at a time, write your answers down, and bring them to your next coaching session. Doing all seven pages in one sitting defeats the point.</p>
              </div></li>
              <li><div>
                <b>Come back to it</b>
                <p>The team and leadership chapters read differently once your role changes. Most people get more out of them a year in than on the first day.</p>
              </div></li>
            </ol>
          </div>
        </section>

        <section className="pfrp__section" style={{ borderTop: 0 }}>
          <div className="pfrp__wrap">
            <div className="pfrp-cta">
              <div>
                <h2>Not sure which report fits?</h2>
                <p>A certified practitioner can talk through what you're trying to achieve and recommend the right depth for the person or the team.</p>
              </div>
              <div className="pfrp-cta__actions">
                <a className="pfrp-btn pfrp-btn--solid" href={links.bookingUrl} target="_blank" rel="noopener noreferrer">Book a conversation</a>
                <Link className="pfrp-btn pfrp-btn--ghost" to="/reports/standard">See the Standard report</Link>
              </div>
            </div>
          </div>
        </section>

        <footer className="pfrp__foot">
          <div className="pfrp__wrap">
            <p>This page is an illustrative preview. Scale positions, sample names and page content shown here are for demonstration only and are not drawn from any individual's report. The FIVE LENS Questionnaire, the Personal Feedback Report and its interpretive content are the property of FIVE LENS Limited. &copy; FIVE LENS Limited.</p>
          </div>
        </footer>
      </div>
    </>
  );
}
